import { format } from "date-fns";
import { Prescription } from "@/types";
import { formatPatientId } from "@/lib/utils/patientId";
import { formatPrescriptionId } from "@/lib/utils/prescriptionId";
import { formatMedicationDuration } from "@/lib/utils/dateUtils";

export function formatLinesAsList(text?: string): string {
  if (!text) return "";
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length === 0) return "";
  const items = lines
    .map((l) => `<li>${l.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</li>`)
    .join("");
  return `<ul class="bullet-list">${items}</ul>`;
}

export interface PrescriptionPrintHtmlInput {
  profile: {
    name: string;
    address?: string;
    phone1?: string;
    phone2?: string;
    phone3?: string;
    email?: string;
    website?: string;
    usePrescriptionHeader?: boolean;
  };
  patientName: string;
  patientAge: string | number;
  patientGender: string;
  prescription: Prescription;
  getDoctorName: (doctorId: string) => string;
  getDoctorRegistration: (doctorId: string) => string;
}

const LETTERHEAD_TOP_PADDING = "1.75in";
const LETTERHEAD_BOTTOM_PADDING = "0.75in";
const PAGE_SIDE_PADDING = "0.5in";

export function buildPrescriptionPrintHtml(input: PrescriptionPrintHtmlInput): string {
  const {
    profile,
    patientName,
    patientAge,
    patientGender,
    prescription: normalizedPrescription,
    getDoctorName,
    getDoctorRegistration,
  } = input;

  const letterheadMode = profile.usePrescriptionHeader === false;
  const pageClass = letterheadMode
    ? "prescription-page letterhead-mode"
    : "prescription-page";

  return `
<!DOCTYPE html>
<html>
<head>
  <title>Prescription - ${patientName}</title>
  <style>
    html, body { margin: 0; padding: 0; height: 100%; }
    body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #000; background: white; }
    .prescription-page { box-sizing: border-box; width: 794px; min-height: 1123px; padding: ${PAGE_SIDE_PADDING}; display: flex; flex-direction: column; background: #ffffff; }
    .prescription-page.letterhead-mode { padding: ${LETTERHEAD_TOP_PADDING} ${PAGE_SIDE_PADDING} ${LETTERHEAD_BOTTOM_PADDING} ${PAGE_SIDE_PADDING}; }
    .prescription-content { flex: 1 1 auto; }
    .header { text-align: center; border-bottom: 2px solid #2563eb; padding-bottom: 15px; margin-bottom: 20px; }
    .hospital-name { font-size: 24px; font-weight: bold; color: #2563eb; margin-bottom: 5px; }
    .hospital-details { font-size: 11px; color: #666; margin-bottom: 5px; }
    .prescription-info { display: flex; justify-content: space-between; margin-bottom: 20px; font-size: 11px; border: 1px solid #ccc; padding: 10px; background-color: #f8fafc; border-radius: 8px; }
    .prescription-info > div { display: flex; flex-direction: column; gap: 5px; }
    .label { font-weight: 700; color: #666; }
    .section { margin-bottom: 20px; }
    .section-row { display: flex; gap: 20px; margin-bottom: 20px; }
    .section-column { flex: 1; }
    .section-title { font-size: 14px; font-weight: bold; margin-bottom: 10px; color: #1e40af; border-left: 3px solid #1e40af; padding-left: 8px; }
    .medication-table { width: 100%; border-collapse: collapse; margin-bottom: 10px; border-radius: 8px; overflow: hidden; }
    .medication-table th { background-color: #1e40af; color: white; font-size: 11px; font-weight: 600; padding: 8px; text-align: left; border: 1px solid #1e3a8a; }
    .medication-table td { padding: 8px; font-size: 11px; border: 1px solid #e5e7eb; vertical-align: top; }
    .medication-table tr:nth-child(even) { background-color: #f8fafc; }
    .medication-name { font-weight: 500; color: #1e40af; }
    .content-box { border: none; padding: 0; font-size: 11px; background-color: transparent; margin-bottom: 10px; }
    .bullet-list { padding-left: 18px; margin: 0; }
    .bullet-list li { margin-bottom: 4px; }
    .signature { margin-top: 40px; text-align: right; display: flex; flex-direction: column; align-items: flex-end; }
    .signature-line { border-top: 1px solid #000; width: 200px; margin-bottom: 5px; margin-left: auto; }
    .footer { flex-shrink: 0; margin-top: auto; padding-top: 12px; font-size: 12px; color: #222; text-align: center; border-top: 1px solid #e5e7eb; page-break-inside: avoid; background: #ffffff; }
    @media print {
      @page { size: A4; margin: 0; }
      body { margin: 0; padding: 0; }
      .prescription-page { width: auto; min-height: auto; padding: ${PAGE_SIDE_PADDING}; display: block; }
      .prescription-page.letterhead-mode { padding: ${LETTERHEAD_TOP_PADDING} ${PAGE_SIDE_PADDING} ${LETTERHEAD_BOTTOM_PADDING} ${PAGE_SIDE_PADDING}; }
      .prescription-content { flex: none; }
      .footer { position: fixed; left: 0; right: 0; bottom: 0; margin-top: 0; padding-top: 8px; }
    }
  </style>
</head>
<body>
  <div class="${pageClass}">
    <div class="prescription-content">
      ${profile.usePrescriptionHeader !== false ? `
      <div class="header">
        <div class="hospital-name">${profile.name}</div>
        <div class="hospital-details">
          ${profile.address || ''}<br>
          Phone: ${profile.phone1 || ''} | Email: ${profile.email || ''}<br>
          ${profile.website || ''}
        </div>
      </div>` : ''}

      <div class="prescription-info">
        <div>
          <div><span class="label">Patient Name:</span> ${patientName}</div>
          <div><span class="label">Age/Gender:</span> ${patientAge} / ${patientGender}</div>
          <div><span class="label">Patient ID:</span> ${formatPatientId(normalizedPrescription.patientId)}</div>
          <div><span class="label">Doctor:</span> ${getDoctorName(normalizedPrescription.doctorId)}</div>
        </div>
        <div>
          <div><span class="label">Prescription ID:</span> ${formatPrescriptionId(normalizedPrescription.id)}</div>
          <div><span class="label">Date:</span> ${format(new Date(normalizedPrescription.createdAt), "dd MMM yyyy")}</div>
        </div>
      </div>

      ${(normalizedPrescription.chief_complaints || normalizedPrescription.clinical_findings) ? `
      <div class="section-row">
        ${normalizedPrescription.chief_complaints ? `
        <div class="section-column">
          <div class="section-title">Chief Complaints</div>
          <div class="content-box">${formatLinesAsList(normalizedPrescription.chief_complaints)}</div>
        </div>` : '<div class="section-column"></div>'}
        ${normalizedPrescription.clinical_findings ? `
        <div class="section-column">
          <div class="section-title">Clinical Findings</div>
          <div class="content-box">${formatLinesAsList(normalizedPrescription.clinical_findings)}</div>
        </div>` : '<div class="section-column"></div>'}
      </div>` : ''}

      ${normalizedPrescription.complaints_duration ? `
      <div class="section">
        <div class="section-title">Duration of Complaints</div>
        <div class="content-box">${normalizedPrescription.complaints_duration}</div>
      </div>` : ''}

      ${(normalizedPrescription.diagnosis || (normalizedPrescription.lab_tests && normalizedPrescription.lab_tests.length > 0)) ? `
      <div class="section-row">
        ${normalizedPrescription.diagnosis ? `
        <div class="section-column">
          <div class="section-title">Diagnosis</div>
          <div class="content-box">${normalizedPrescription.diagnosis}</div>
        </div>` : '<div class="section-column"></div>'}
        ${normalizedPrescription.lab_tests && normalizedPrescription.lab_tests.length > 0 ? `
        <div class="section-column">
          <div class="section-title">Laboratory Tests</div>
          <div class="content-box">
            ${Array.isArray(normalizedPrescription.lab_tests)
              ? normalizedPrescription.lab_tests.map((test, index) => `${index + 1}. ${test}`).join('<br>')
              : normalizedPrescription.lab_tests}
          </div>
        </div>` : '<div class="section-column"></div>'}
      </div>` : ''}

      ${normalizedPrescription.medications && normalizedPrescription.medications.length > 0 ? `
      <div class="section">
        <div class="section-title">Medications</div>
        <table class="medication-table">
          <thead>
            <tr>
              <th>Medication</th><th>Dosage</th><th>Frequency</th><th>Duration</th><th>Instructions</th>
            </tr>
          </thead>
          <tbody>
            ${normalizedPrescription.medications.map((med) => `
            <tr>
              <td class="medication-name">${med.name}</td>
              <td>${med.dosage} ${(med as any).doseType ? (med as any).doseType : ''}</td>
              <td>${med.frequency || 'No frequency'}</td>
              <td>${formatMedicationDuration(med.duration)}</td>
              <td style="font-size: 11px;">${med.instructions || '-'}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>` : ''}

      ${normalizedPrescription.notes ? `
      <div class="section">
        <div class="section-title">Additional Notes</div>
        <div class="content-box">${normalizedPrescription.notes}</div>
      </div>` : ''}

      ${normalizedPrescription.instructions && normalizedPrescription.instructions.trim() !== '' && !normalizedPrescription.instructions.toLowerCase().includes('draft prescription - instructions to be added') ? `
      <div class="section">
        <div class="section-title">Instructions</div>
        <div class="content-box">${normalizedPrescription.instructions}</div>
      </div>` : ''}

      ${normalizedPrescription.follow_up_date ? `
      <div class="section">
        <div style="display: flex; align-items: center; gap: 8px;">
          <span class="section-title" style="margin-bottom: 0;">Follow-up Date:</span>
          <span style="font-weight: bold;">${format(new Date(normalizedPrescription.follow_up_date), "dd MMM yyyy")}</span>
        </div>
      </div>` : ''}

      <div class="signature">
        <div class="signature-line"></div>
        <div><strong>Dr. ${getDoctorName(normalizedPrescription.doctorId)}</strong></div>
        ${getDoctorRegistration(normalizedPrescription.doctorId) ? `<div style="font-size: 11px; color: #555;">Reg. No: ${getDoctorRegistration(normalizedPrescription.doctorId)}</div>` : ''}
      </div>
    </div>

    ${profile.usePrescriptionHeader !== false ? `
    <div class="footer">
      <div>
        <span style="font-weight: 700;">For Appointments:</span> ${profile.phone1 || '-'}
         | <span style="font-weight: 700;">Alt:</span> ${profile.phone2 || '-'}
         | <span style="font-weight: 700;">Emergency contact:</span> ${profile.phone3 || '-'}
      </div>
    </div>` : ''}
  </div>
</body>
</html>`;
}
